import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { WIZARD_STEPS } from './types';
import { useUploadWizard } from './hooks/useUploadWizard';
import FileUploadStep from './steps/FileUploadStep';
import DetailsStep from './steps/DetailsStep';
import ReviewStep from './steps/ReviewStep';
import SequenceUploadSuccessModal from './SequenceUploadSuccessModal';

interface SequenceUploadWizardProps {
  onSuccess?: (sequenceId: string) => void;
  onCancel?: () => void;
  onStepChange?: (currentStep: number) => void;
}

const SequenceUploadWizard: React.FC<SequenceUploadWizardProps> = ({ 
  onSuccess, 
  onCancel,
  onStepChange
}) => {
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<{ sequenceId: string; title: string } | null>(null);

  const handleWizardSuccess = (sequenceId: string, sequenceData: any) => {
    setSuccessData({
      sequenceId,
      title: sequenceData.title || sequenceData.name
    });
    setShowSuccessModal(true);
    // Don't call onSuccess immediately - wait for user action in modal
  };

  const handleUploadAnother = () => {
    setShowSuccessModal(false);
    setSuccessData(null);
    // Reset the wizard to the first step
    wizard.goToStep(0);
    // Clear the form and uploaded file
    wizard.form.reset({
      is_free: false,
      price: 0,
      software_type: 'xLights',
      holiday_type: 'Christmas',
    });
    wizard.handleFileRemove();
  };

  const handleViewSequence = () => {
    if (successData) {
      setShowSuccessModal(false);
      navigate(`/sequence/${successData.sequenceId}`);
    }
  };

  const wizard = useUploadWizard(handleWizardSuccess, onCancel);
  
  // Notify parent component when step changes
  React.useEffect(() => {
    onStepChange?.(wizard.currentStep);
  }, [wizard.currentStep, onStepChange]);
  
  const progressPercentage = ((wizard.currentStep + 1) / wizard.totalSteps) * 100;

  const renderCurrentStep = () => {
    switch (wizard.getCurrentStepId()) {
      case 'file':
        return (
          <FileUploadStep
            uploadedFile={wizard.uploadedFile}
            onFileSelect={wizard.handleFileSelect}
            onFileRemove={wizard.handleFileRemove}
            onNext={wizard.goNext}
            uploadProgress={wizard.uploadProgress}
            isUploading={wizard.isUploading}
          />
        );
      
      case 'details':
        return (
          <DetailsStep
            form={wizard.form}
            selectedTrack={wizard.selectedSpotifyTrack}
            onTrackSelect={wizard.handleSpotifyTrackSelect}
            layoutPhotos={wizard.layoutPhotos}
            onLayoutPhotosChange={wizard.handleLayoutPhotosChange}
            onNext={wizard.goNext}
            onBack={wizard.goBack}
          />
        );
      
      case 'review':
        return (
          <ReviewStep
            form={wizard.form}
            uploadedFile={wizard.uploadedFile}
            selectedTrack={wizard.selectedSpotifyTrack}
            onSubmit={wizard.handleSubmit}
            onBack={wizard.goBack}
            isSubmitting={wizard.isSubmitting}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Progress Header */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Upload New Sequence</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Step {wizard.currentStep + 1} of {wizard.totalSteps}: {wizard.currentStepInfo.label}
                </p>
              </div>
            </div>
            
            <Progress value={progressPercentage} className="h-2" />
            
            {/* Step Indicators */}
            <div className="flex justify-between">
              {WIZARD_STEPS.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex flex-col items-center cursor-pointer transition-colors ${
                    index === wizard.currentStep
                      ? 'text-primary'
                      : index < wizard.currentStep
                      ? 'text-green-600'
                      : 'text-gray-400'
                  }`}
                  onClick={() => {
                    // Only allow going back to previous steps
                    if (index < wizard.currentStep) {
                      wizard.goToStep(index);
                    }
                  }}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors ${
                      index === wizard.currentStep
                        ? 'border-primary bg-primary text-white'
                        : index < wizard.currentStep
                        ? 'border-green-600 bg-green-600 text-white'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {index < wizard.currentStep ? '✓' : index + 1}
                  </div>
                  <span className="text-xs mt-1 hidden sm:block">{step.label}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Current Step Content */}
        <div className="animate-in fade-in-0 slide-in-from-bottom-4 duration-300">
          {renderCurrentStep()}
        </div>
      </div>

      {/* Success Modal */}
      {successData && (
        <SequenceUploadSuccessModal
          isOpen={showSuccessModal}
          sequenceTitle={successData.title}
          sequenceId={successData.sequenceId}
          onUploadAnother={handleUploadAnother}
          onViewSequence={handleViewSequence}
        />
      )}
    </>
  );
};

export default SequenceUploadWizard;